import React, { useEffect, useState, useRef } from 'react';
import { getLeftMenus, getManager, getPortlets, updateManager } from '../../api/managerApi';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  MANAGER_MENU_ITEMS,
  managerFieldLabels,
  managerLFields,
  managerMFields,
  managerPortalPageFields,
  managerBooleanRadioFields,
  managerFileFields,
  managerRadioOptionsMap,
  managerToBoolean,
  managerIsRadioChecked,
  managerInitialForm,
  managerToLayoutNumber,
  parseManagerSelectedPortlet,
  parseManagerSelectedMenus,
  serializeManagerSelectedPortlet,
  serializeManagerSelectedMenus,
  setManagerMenuItemsFromDb,
  setPortletItemsFromDb,
} from '../../utils/managerUtils';
import PortletLayoutSelector from '../../components/manager/PortletLayoutSelector';
import MenuListSelector from '../../components/manager/MenuListSelector';
import '../../css/manager.css';

const booleanRadioFields = managerBooleanRadioFields;
const fileFields = managerFileFields;
const radioOptionsMap = managerRadioOptionsMap;
const toBoolean = managerToBoolean;
const isRadioChecked = managerIsRadioChecked;

const ManagerEdit = () => {
  const { id } = useParams();
  const location = useLocation();
  const [form, setForm] = useState(managerInitialForm);
  const [files, setFiles] = useState({});
  const [deletedFiles, setDeletedFiles] = useState(new Set());
  const originalFilePaths = useRef({});
  const [selectedLayout, setSelectedLayout] = useState(6);
  const [zoneData, setZoneData] = useState({});
  const [selectedMenus, setSelectedMenus] = useState([]);
  const [availableMenus, setAvailableMenus] = useState(MANAGER_MENU_ITEMS);
  const navigate = useNavigate();

  const modeParam = new URLSearchParams(location.search).get('mode');
  const editMode = ['basic', 'portlet', 'menu'].includes(modeParam) ? modeParam : 'basic';
  const showBasicSections = editMode === 'basic';
  const showPortletSection = editMode === 'portlet';
  const showMenuSection = editMode === 'menu';
  const modeTitle =
    editMode === 'portlet' ? '포틀릿 편집' :
    editMode === 'menu' ? '메뉴 편집' :
    '기본 편집';

  const handleFileChange = e => {
    const { name, files: selected } = e.target;
    setFiles(prev => ({ ...prev, [name]: selected[0] }));
  };

  useEffect(() => {
    Promise.all([getManager(id), getLeftMenus(), getPortlets()])
      .then(([data, menuRows, portletRows]) => {
        const resolvedMenus = setManagerMenuItemsFromDb(menuRows);
        setAvailableMenus(resolvedMenus);
        setPortletItemsFromDb(portletRows);

        const normalized = { ...managerInitialForm, ...data };
        booleanRadioFields.forEach(field => {
          normalized[field] = toBoolean(normalized[field]);
        });
        fileFields.forEach(field => {
          if (normalized[field]) originalFilePaths.current[field] = normalized[field];
        });

        const nextLayout = managerToLayoutNumber(normalized.P_PortletTypeNum);
        const nextZoneData = parseManagerSelectedPortlet(normalized.P_SelectPortlet, nextLayout);
        const nextMenus = parseManagerSelectedMenus(normalized.P_SelectMenus, resolvedMenus);

        setSelectedLayout(nextLayout);
        setZoneData(nextZoneData);
        setSelectedMenus(nextMenus);
        setForm({
          ...normalized,
          P_PortletTypeNum: nextLayout,
        });
      })
      .catch((err) => {
        console.error('회사/좌측메뉴/포틀릿 정보 조회 실패:', err);
      });
  }, [id]);

  useEffect(() => {
    setForm(prev => ({ ...prev, P_PortletTypeNum: selectedLayout }));
  }, [selectedLayout]);

  useEffect(() => {
    setForm(prev => ({
      ...prev,
      P_SelectPortlet: serializeManagerSelectedPortlet(zoneData),
    }));
  }, [zoneData]);

  useEffect(() => {
    setForm(prev => ({
      ...prev,
      P_SelectMenus: serializeManagerSelectedMenus(selectedMenus),
    }));
  }, [selectedMenus]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]:
        type === 'radio'
          ? (booleanRadioFields.includes(name) ? value === 'true' : value)
          : type === 'checkbox'
            ? checked
            : value
    });
  };

  const handleFileDelete = (field) => {
    setForm(prev => ({ ...prev, [field]: '' }));
    setFiles(prev => { const next = { ...prev }; delete next[field]; return next; });
    setDeletedFiles(prev => new Set([...prev, field]));
  };

  const handleFileDeleteCancel = (field) => {
    setDeletedFiles(prev => { const next = new Set(prev); next.delete(field); return next; });
    setForm(prev => ({ ...prev, [field]: originalFilePaths.current[field] || '' }));
  };

  const handleSubmit = async e => {
    if (e) e.preventDefault();
    const formData = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (!fileFields.includes(k)) formData.append(k, v ?? '');
    });
    fileFields.forEach(field => {
      if (files[field]) {
        formData.append(field, files[field]);
      } else if (deletedFiles.has(field)) {
        formData.append(field, '__DELETE__');
      }
    });
    await updateManager(id, formData);
    window.dispatchEvent(new CustomEvent('managerDataChanged'));
    navigate(`/manager/${id}`);
  };

  const renderFileInput = (field) => {
    const isDeleted = deletedFiles.has(field);
    const currentPath = form[field];
    return (
      <div>
        {currentPath && !isDeleted && (
          <div className="d-flex align-items-center gap-2 mb-1">
            <small className="text-muted">현재: {currentPath}</small>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger py-0 px-1"
              onClick={() => handleFileDelete(field)}
            >
              삭제
            </button>
          </div>
        )}
        {isDeleted && (
          <div className="d-flex align-items-center gap-2 mb-1">
            <small className="text-danger">삭제 예정</small>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary py-0 px-1"
              onClick={() => handleFileDeleteCancel(field)}
            >
              취소
            </button>
          </div>
        )}
        {!isDeleted && (
          <input
            type="file"
            name={field}
            onChange={handleFileChange}
            className="form-control"
            accept="image/*"
          />
        )}
      </div>
    );
  };

  return (
    <div className="container-fluid view-manager">
      <div className="row">
        <div className="col-12">
          {/* 상단 버튼 영역 */}
          <div className="manager-header-fixed mb-3 d-flex justify-content-between align-items-center">
            <div className="fw-bold text-dark">{form.M_ComName} {modeTitle}</div>
            <div className="d-flex gap-2">
              <button className="btn btn-primary" onClick={handleSubmit}>저장</button>
              <button className="btn btn-outline-secondary" onClick={() => navigate(`/manager/${id}`)}>취소</button>
            </div>
          </div>

          {/* M_ 필드 정보 */}
          {showBasicSections && <div className="manager-body manager-m mb-4 mt-3">
            <h6 className="fw-bold mb-3">회사기본 정보</h6>
            <div className="row">
              {managerMFields.map(field => (
                <div className="col-md-6 mb-2" key={field}>
                  <label className="form-label">{managerFieldLabels[field]}</label>
                  {radioOptionsMap[field] ? (
                    <div className="d-flex gap-3 align-items-center pt-1">
                      {radioOptionsMap[field].map(opt => (
                        <label key={opt.value} className="form-check-label d-flex align-items-center gap-1">
                          <input
                            type="radio"
                            name={field}
                            value={opt.value}
                            checked={isRadioChecked(field, opt.value, form[field])}
                            onChange={handleChange}
                            className="form-check-input"
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  ) : fileFields.includes(field) ? (
                    renderFileInput(field)
                  ) : (
                    <input
                      name={field}
                      value={form[field]}
                      onChange={handleChange}
                      className="form-control"
                      type={field.includes('PW') ? 'password' : (field.includes('Port') || field.includes('Size')) ? 'number' : 'text'}
                      required={['M_ComCode','M_ComName','M_Domain','M_DBName','M_DBHost','M_DBUser','M_DBPW'].includes(field)}
                      disabled={field === 'M_ComCode'}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>}

          {/* L_ 필드 정보 */}
          {showBasicSections && <div className="manager-body manager-l mb-4">
            <h6 className="fw-bold mb-3">로그인 페이지 정보</h6>
            <div className="row">
              {managerLFields.map(field => (
                <div className="col-md-6 mb-2" key={field}>
                  <label className="form-label">{managerFieldLabels[field]}</label>
                  {radioOptionsMap[field] ? (
                    <div className="d-flex gap-3 align-items-center pt-1">
                      {radioOptionsMap[field].map(opt => (
                        <label key={opt.value} className="form-check-label d-flex align-items-center gap-1">
                          <input
                            type="radio"
                            name={field}
                            value={opt.value}
                            checked={isRadioChecked(field, opt.value, form[field])}
                            onChange={handleChange}
                            className="form-check-input"
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  ) : fileFields.includes(field) ? (
                    renderFileInput(field)
                  ) : (
                    <input
                      name={field}
                      value={form[field]}
                      onChange={handleChange}
                      className="form-control"
                      type="text"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>}

          {/* P_ 필드 정보 */}
          {showBasicSections && <div className="manager-body manager-p mb-4">
            <h6 className="fw-bold mb-3">포탈 페이지 정보</h6>
            <div className="row">
              {managerPortalPageFields
                .filter(field => field !== 'P_SelectMenus')
                .map(field => (
                <div className="col-md-6 mb-2" key={field}>
                  <label className="form-label">{managerFieldLabels[field]}</label>
                  {radioOptionsMap[field] ? (
                    <div className="d-flex gap-3 align-items-center pt-1">
                      {radioOptionsMap[field].map(opt => (
                        <label key={opt.value} className="form-check-label d-flex align-items-center gap-1">
                          <input
                            type="radio"
                            name={field}
                            value={opt.value}
                            checked={isRadioChecked(field, opt.value, form[field])}
                            onChange={handleChange}
                            className="form-check-input"
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  ) : fileFields.includes(field) ? (
                    renderFileInput(field)
                  ) : (
                    <input
                      name={field}
                      value={form[field]}
                      onChange={handleChange}
                      className="form-control"
                      type={field.includes('Num') ? 'number' : 'text'}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>}

          {showPortletSection && <div className="manager-body manager-p mb-4">
            <h6 className="fw-bold mb-3">포탈 포틀릿 정보</h6>
            <PortletLayoutSelector
              selectedLayout={selectedLayout}
              setSelectedLayout={setSelectedLayout}
              zoneData={zoneData}
              setZoneData={setZoneData}
            />
          </div>}

          {showMenuSection && <div className="manager-body manager-p mb-4">
            <h6 className="fw-bold mb-3">좌측메뉴정보</h6>
            <MenuListSelector
              selectedMenus={selectedMenus}
              setSelectedMenus={setSelectedMenus}
              menuItems={availableMenus}
            />
          </div>}

        </div>
      </div>
    </div>
  );
};

export default ManagerEdit;
